import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import { featuredPropertiesService } from '../../../services/featuredPropertiesService';

const PropertySelectionModal = ({
  isOpen,
  onClose,
  onSave
}) => {
  const { user, userProfile } = useAuth();
  
  const [availableProperties, setAvailableProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState('all');
  const [propertyType, setPropertyType] = useState('all');
  
  // Form data for featured property
  const [formData, setFormData] = useState({
    display_order: 1,
    is_active: true,
    custom_headline: '',
    promotional_text: '',
    hero_image_url: '',
    listing_type: ''
  });

  // Check if user is authorized
  const isAuthorized = user && userProfile && (userProfile?.role === 'admin' || userProfile?.role === 'agent');

  // Load available properties
  const loadAvailableProperties = async () => {
    if (!isAuthorized) {
      setError('Vous devez être connecté avec des permissions administrateur ou agent pour accéder aux propriétés.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await featuredPropertiesService?.getAvailableProperties();
      
      if (fetchError) {
        setError(`Erreur lors du chargement: ${fetchError}`);
        return;
      }
      
      setAvailableProperties(data || []);
      setFilteredProperties(data || []);
    } catch (err) {
      if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')) {
        setError('Impossible de se connecter à la base de données. Votre projet Supabase pourrait être en pause.');
        return;
      }
      setError(`Erreur inattendue: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (!isAuthorized) {
        setError('Accès refusé. Seuls les administrateurs et agents peuvent créer des propriétés en vedette.');
        return;
      }
      loadAvailableProperties();
    }
  }, [isOpen, isAuthorized]);

  // Apply filters
  useEffect(() => {
    let filtered = [...availableProperties];

    // Search filter
    if (searchTerm) {
      filtered = filtered?.filter(property =>
        property?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        property?.address?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        property?.commune?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Price range filter
    if (priceRange !== 'all') {
      filtered = filtered?.filter(property => {
        const price = property?.price || 0;
        switch (priceRange) {
          case 'under_50m':
            return price < 50000000;
          case '50m_100m':
            return price >= 50000000 && price < 100000000;
          case '100m_200m':
            return price >= 100000000 && price < 200000000;
          case 'over_200m':
            return price >= 200000000;
          default:
            return true;
        }
      });
    }

    // Property type filter
    if (propertyType !== 'all') {
      filtered = filtered?.filter(property => 
        property?.property_type === propertyType
      );
    }

    setFilteredProperties(filtered);
  }, [availableProperties, searchTerm, priceRange, propertyType]);

  const formatPrice = (price) => {
    if (!price) return 'Prix non défini';
    const numPrice = parseInt(price);
    const formatted = (numPrice / 1000000)?.toFixed(0);
    return `${formatted}M XOF`;
  };

  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    setFormData(prev => ({
      ...prev,
      custom_headline: property?.title || '',
      promotional_text: `Découvrez cette ${property?.property_type} exceptionnelle`,
      listing_type: property?.market_type || 'sale'
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!selectedProperty) {
      setError('Veuillez sélectionner une propriété');
      return;
    }

    if (!isAuthorized) {
      setError('Vous n\'avez pas les permissions nécessaires pour créer une propriété en vedette.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const submitData = {
        property_id: selectedProperty?.id,
        ...formData,
        listing_type: formData?.listing_type || null,
        promotional_price: formData?.promotional_price ? parseFloat(formData?.promotional_price) : null
      };

      const { error: createError } = await featuredPropertiesService?.createFeaturedProperty(submitData);
      
      if (createError) {
        setError(createError);
        return;
      }

      onSave?.();
      onClose();
    } catch (err) {
      if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')) {
        setError('Impossible de se connecter à la base de données. Veuillez vérifier votre connexion.');
        return;
      }
      setError(`Erreur inattendue: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Show unauthorized access message
  if (!isAuthorized) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-deep max-w-md w-full p-8 text-center">
          <Icon name="ShieldAlert" size={64} className="text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-text-primary mb-4">
            Accès Restreint
          </h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">
              Seuls les administrateurs et agents peuvent créer des propriétés en vedette.
            </p>
          </div>
          
          {!user ? (
            <div className="space-y-4">
              <p className="text-text-secondary mb-6">
                Vous devez être connecté avec un compte administrateur ou agent.
              </p>
              <Button
                variant="default"
                onClick={() => window.location.href = '/login'}
              >
                Se connecter
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-text-secondary mb-6">
                Votre compte ({userProfile?.role || 'client'}) ne dispose pas des permissions nécessaires.
              </p>
              <Button
                variant="outline"
                onClick={onClose}
              >
                Fermer
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-deep max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-heading font-bold text-text-primary">
              Ajouter une Propriété en Vedette
            </h2>
            <p className="text-text-secondary mt-1">
              Sélectionnez une propriété à mettre en vedette sur la page d'accueil
            </p>
            <div className="mt-2 flex items-center gap-2 text-sm text-text-secondary">
              <Icon name="User" size={14} />
              <span>Connecté: {userProfile?.full_name} ({userProfile?.role})</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        <div className="flex h-[calc(90vh-200px)]">
          {/* Left Panel - Property Selection */}
          <div className="flex-1 p-6 border-r overflow-y-auto">
            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Icon name="AlertCircle" size={20} className="text-red-600 mr-3" />
                    <span className="text-red-800">{error}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    iconName="X"
                  />
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-surface rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-text-primary mb-4">Filtres</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Rechercher
                  </label>
                  <Input
                    type="text"
                    placeholder="Titre, adresse, commune..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    iconName="Search"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Prix
                  </label>
                  <Select
                    value={priceRange}
                    onChange={(e) => setPriceRange(e?.target?.value)}
                  >
                    <option value="all">Tous les prix</option>
                    <option value="under_50m">Moins de 50M</option>
                    <option value="50m_100m">50M - 100M</option>
                    <option value="100m_200m">100M - 200M</option>
                    <option value="over_200m">Plus de 200M</option>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Type
                  </label>
                  <Select
                    value={propertyType}
                    onChange={(e) => setPropertyType(e?.target?.value)}
                  >
                    <option value="all">Tous les types</option>
                    <option value="apartment">Appartement</option>
                    <option value="house">Maison</option>
                    <option value="villa">Villa</option>
                    <option value="land">Terrain</option>
                    <option value="commercial">Commercial</option>
                  </Select>
                </div>
              </div>
            </div>

            {/* Properties List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" size={48} className="animate-spin text-brand-earth" />
              </div>
            ) : filteredProperties?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Home" size={48} className="text-text-secondary mx-auto mb-4" />
                <p className="text-text-secondary">Aucune propriété disponible</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredProperties?.map((property) => (
                  <div
                    key={property?.id}
                    onClick={() => handlePropertySelect(property)}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      selectedProperty?.id === property?.id
                        ? 'border-brand-earth bg-brand-earth/5' :'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex gap-4">
                      <img
                        src={property?.images?.[0] || '/assets/images/no_image.png'}
                        alt={property?.title}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-text-primary mb-1 line-clamp-1">
                          {property?.title}
                        </h3>
                        
                        <div className="flex items-center text-sm text-text-secondary mb-2">
                          <Icon name="MapPin" size={14} className="mr-1" />
                          <span className="line-clamp-1">
                            {property?.address} • {property?.commune?.name}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm text-text-secondary">
                            <div className="flex items-center">
                              <Icon name="Bed" size={14} className="mr-1" />
                              {property?.bedrooms || 0}
                            </div>
                            <div className="flex items-center">
                              <Icon name="Bath" size={14} className="mr-1" />
                              {property?.bathrooms || 0}
                            </div>
                            <div className="flex items-center">
                              <Icon name="Square" size={14} className="mr-1" />
                              {property?.surface_area || 0}m²
                            </div>
                          </div>
                          
                          <span className="font-bold text-brand-earth">
                            {formatPrice(property?.price)}
                          </span>
                        </div>
                      </div>
                      
                      {selectedProperty?.id === property?.id && (
                        <div className="flex-shrink-0">
                          <Icon name="Check" size={20} className="text-brand-earth" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel - Configuration */}
          <div className="w-96 p-6 bg-surface">
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              Configuration
            </h3>
            
            {selectedProperty ? (
              <div className="space-y-4">
                {/* Selected Property Preview */}
                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={selectedProperty?.images?.[0] || '/assets/images/no_image.png'}
                      alt={selectedProperty?.title}
                      className="w-12 h-12 object-cover rounded"
                    />
                    <div>
                      <h4 className="font-medium text-text-primary text-sm">
                        {selectedProperty?.title}
                      </h4>
                      <p className="text-xs text-text-secondary">
                        {formatPrice(selectedProperty?.price)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Ordre d'Affichage
                    </label>
                    <Input
                      type="number"
                      min="1"
                      value={formData?.display_order}
                      onChange={(e) => handleInputChange('display_order', parseInt(e?.target?.value) || 1)}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Type d'Annonce
                    </label>
                    <Select
                      value={formData?.listing_type || ''}
                      onChange={(e) => handleInputChange('listing_type', e?.target?.value || null)}
                    >
                      <option value="">Utiliser le type de la propriété</option>
                      <option value="sale">À Vendre</option>
                      <option value="rental">À Louer</option>
                    </Select>
                    <p className="text-xs text-text-secondary mt-1">
                      Si non défini, le type de la propriété sera utilisé
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Titre Personnalisé
                    </label>
                    <Input
                      type="text"
                      placeholder="Titre accrocheur"
                      value={formData?.custom_headline}
                      onChange={(e) => handleInputChange('custom_headline', e?.target?.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Texte Promotionnel
                    </label>
                    <textarea
                      className="w-full p-3 border border-input rounded-lg resize-none text-sm"
                      rows={3}
                      placeholder="Message marketing"
                      value={formData?.promotional_text}
                      onChange={(e) => handleInputChange('promotional_text', e?.target?.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      URL Image Hero (Optionnel)
                    </label>
                    <Input
                      type="url"
                      placeholder="https://..."
                      value={formData?.hero_image_url}
                      onChange={(e) => handleInputChange('hero_image_url', e?.target?.value)}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="MousePointer" size={48} className="text-text-secondary mx-auto mb-4" />
                <p className="text-text-secondary">
                  Sélectionnez une propriété pour configurer sa mise en vedette
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={!selectedProperty || saving || !isAuthorized}
            iconName={saving ? "Loader2" : "Star"}
            className={saving ? "animate-spin" : ""}
            title={!isAuthorized ? 'Permissions insuffisantes' : ''}
          >
            {saving ? 'Ajout en cours...' : 'Mettre en Vedette'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PropertySelectionModal;